import React, { useEffect, useRef, useState } from "react";

interface LicensesSectionProps {
  isEditable: boolean;
  selectedAssesmentData: any;
  value?: { netNewLicenses: number; totalLicenses: number };
  /**
   * Second arg is which field the user edited (matches calculator: net new drives total).
   */
  onChange?: (
    next: { netNewLicenses: number; totalLicenses: number },
    changedField: "netNewLicenses" | "totalLicenses"
  ) => void;
}

const LicensesSection: React.FC<LicensesSectionProps> = ({
  isEditable,
  selectedAssesmentData,
  value,
  onChange,
}) => {
  const controlled = value != null && onChange != null;

  const [formData, setFormData] = useState({
    netNewLicenses: selectedAssesmentData?.partner_new_licenses ?? 0,
    totalLicenses: selectedAssesmentData?.partner_total_licenses ?? 0,
  });
  const [draftInput, setDraftInput] = useState<{
    netNewLicenses: string | null;
    totalLicenses: string | null;
  }>({
    netNewLicenses: null,
    totalLicenses: null,
  });

  const netNewInputRef = useRef<HTMLInputElement>(null);
  const totalLicensesInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!controlled) {
      setFormData({
        netNewLicenses: selectedAssesmentData?.partner_new_licenses ?? 0,
        totalLicenses: selectedAssesmentData?.partner_total_licenses ?? 0,
      });
    }
    setDraftInput({
      netNewLicenses: null,
      totalLicenses: null,
    });
  }, [selectedAssesmentData, controlled]);

  useEffect(() => {
    const preventWheel = (e: WheelEvent) => {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    };
    const netEl = netNewInputRef.current;
    const totalEl = totalLicensesInputRef.current;
    if (netEl) {
      netEl.addEventListener("wheel", preventWheel, { passive: false });
    }
    if (totalEl) {
      totalEl.addEventListener("wheel", preventWheel, { passive: false });
    }
    return () => {
      if (netEl) netEl.removeEventListener("wheel", preventWheel);
      if (totalEl) totalEl.removeEventListener("wheel", preventWheel);
    };
  }, [isEditable]);

  const display = controlled ? value : formData;

  const licenseData = [
    {
      label: "Net New Licenses",
      value: display.netNewLicenses,
      field: "netNewLicenses" as const,
      inputRef: netNewInputRef,
    },
    {
      label: "Total Licenses",
      value: display.totalLicenses,
      field: "totalLicenses" as const,
      inputRef: totalLicensesInputRef,
    },
  ];

  const handleInputChange = (
    field: "netNewLicenses" | "totalLicenses",
    raw: string
  ) => {
    const sanitized = raw.replace(/[^\d]/g, "");
    const isEmpty = sanitized === "";
    const num = isEmpty ? 0 : Math.max(0, Number(sanitized) || 0);

    setDraftInput((prev) => ({
      ...prev,
      [field]: isEmpty ? "" : sanitized,
    }));

    if (controlled) {
      const next = {
        ...value!,
        [field]: num,
      } as { netNewLicenses: number; totalLicenses: number };
      onChange!(next, field);
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: num,
      }));
    }
  };

  return (
    <div className="detail-section">
      <h2>Licenses</h2>
      <div className="form-grid">
        {licenseData.map((item, index) => (
          <div key={index} className="form-field">
            <label>{item.label}</label>
            {isEditable ? (
              <input
                ref={item.inputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input hide-number-spinner"
                value={draftInput[item.field] !== null ? draftInput[item.field] : item.value}
                onChange={(e) => handleInputChange(item.field, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown")
                    e.preventDefault();
                }}
                onBlur={() =>
                  setDraftInput((prev) => ({ ...prev, [item.field]: null }))
                }
              />
            ) : (
              <div className="form-value">{item.value ?? 0}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LicensesSection;
